import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, useDocument, useI18n, useSignature } from '@react/hooks';
import { Button, ErrorAlert } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { useScope } from '@/hooks/useScope';
import { SigningProcedureDialog } from '@/features/signature/SigningProcedureDialog';
import type { DocumentVersion } from '@core/types/document';
import type { ContractSigningContext, Party, Signature } from '@core/types/signature';

interface DocumentSigningPanelProps {
  documentId: string;
  productId: string;
}

export function DocumentSigningPanel({ documentId, productId }: DocumentSigningPanelProps) {
  const { t } = useI18n();
  const { activeProfileId } = useAuth();
  const scope = useScope();
  const documentStore = useDocument();
  const signatureStore = useSignature();
  const [version, setVersion] = useState<DocumentVersion>();
  const [parties, setParties] = useState<Party[]>([]);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [wizardParty, setWizardParty] = useState<Party>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [versionsResponse, partiesResponse, signaturesResponse] = await Promise.all([
        documentStore.ListVersions('document', documentId),
        signatureStore.ListPartiesByTarget('document', documentId),
        signatureStore.ListSignaturesByTarget('document', documentId),
      ]);
      const versions = versionsResponse.versions?.versions ?? [];
      const signingVersion = versions
        .filter((item) => item.content_hash)
        .sort((left, right) => right.version_number - left.version_number)[0];
      setVersion(signingVersion);
      setParties(partiesResponse.items ?? []);
      setSignatures(signaturesResponse.items ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [documentId, documentStore, signatureStore]);

  useEffect(() => { load(); }, [load]);

  const currentParty = useMemo(() => parties.find((party) =>
    party.represented_by?.entity?.id === activeProfileId || party.party_actor?.entity?.id === activeProfileId,
  ), [parties, activeProfileId]);

  const openWizard = async () => {
    setBusy(true);
    setError(null);
    try {
      if (currentParty) {
        setWizardParty(currentParty);
        return;
      }
      const target = { type_id: '', id: documentId, type_name: 'document', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: productId };
      const actor = {
        entity: { type_id: '', id: activeProfileId ?? '', type_name: 'profile', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: productId },
        display_name: '',
        avatar: '',
      };
      const response = await signatureStore.CreateParty({ party: {
        role: 'PARTY_ROLE_OTHER',
        role_label: '',
        display_name: t('document.signing.currentUser'),
        target,
        party_actor: actor,
        represented_by: actor,
        sign_order: parties.length + 1,
        decline_reason: '',
        signature_method_allowed: ['SIGNATURE_METHOD_INTERNAL_QUALIFIED'],
        external_email: '',
        external_phone: '',
      } });
      if (response.party) {
        setParties((current) => [...current, response.party!]);
        setWizardParty(response.party);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const sign = async (context: ContractSigningContext) => {
    if (!wizardParty?.entity?.id || !version?.content_hash) return;
    setBusy(true);
    setError(null);
    try {
      const created = await signatureStore.CreateSignatureRequest({ signature_request: {
        signed_hash_algo: 'sha256',
        content_hash: version.content_hash,
        target: { type_id: '', id: documentId, type_name: 'document', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: productId },
        party_ref: wizardParty.entity,
        document_version: version.version_number,
        signing_context_ref: context.entity,
      } });
      const request = created.signature_request;
      if (!request?.entity?.id) throw new Error(t('document.signing.requestError'));
      const submitted = await signatureStore.SubmitSignature(request.entity.id, {
        signature_request: request.entity,
        content_hash: version.content_hash,
      });
      if (submitted.signature?.entity?.id) {
        await signatureStore.ValidateSignature(submitted.signature.entity.id, { signature: submitted.signature.entity });
      }
      setWizardParty(undefined);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const validate = async (signature: Signature) => {
    if (!signature.entity?.id) return;
    setBusy(true);
    setError(null);
    try {
      await signatureStore.ValidateSignature(signature.entity.id, { signature: signature.entity });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="py-3 text-sm text-muted-foreground">{t('common.loading')}</p>;

  return (
    <div className="space-y-4">
      {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      <section className="space-y-2 rounded-md border border-border p-3">
        <h3 className="text-sm font-medium text-foreground">{t('document.signing.immutableVersion')}</h3>
        {version ? (
          <>
            <p className="text-sm text-muted-foreground">{t('document.signing.version', { version: version.version_number })}</p>
            <p className="break-all font-mono text-xs text-muted-foreground" title={version.content_hash}>{version.content_hash}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t('document.signing.noVersion')}</p>
        )}
      </section>

      <Button type="button" className="w-full" disabled={!version || busy} onClick={openWizard}>
        {busy ? t('common.saving') : t('document.signing.signDocument')}
      </Button>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">{t('document.signing.parties')}</h3>
        {parties.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('document.signing.noParties')}</p>
        ) : (
          <ul className="space-y-1">
            {parties.map((party) => <li key={party.entity?.id} className="text-sm text-foreground">{party.display_name}</li>)}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">{t('document.signing.evidence')}</h3>
        {signatures.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('document.signing.noEvidence')}</p>
        ) : (
          <ul className="space-y-2">
            {signatures.map((signature) => {
              const valid = signature.status === 'SIGNATURE_STATUS_VALID';
              return (
                <li key={signature.entity?.id} className="space-y-2 rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <StatusBadge variant={valid ? 'success' : 'warning'} label={t(valid ? 'document.signing.valid' : 'document.signing.pending')} size="sm" />
                    <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => validate(signature)}>{t('document.signing.validate')}</Button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t('document.signing.version', { version: signature.document_version })}</p>
                  <p className="break-all font-mono text-xs text-muted-foreground">{signature.content_hash}</p>
                  <p className="text-xs text-muted-foreground">{t('document.signing.certificates', { count: signature.evidence?.qualified?.cert_chain_pem?.length ?? 0 })}</p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {wizardParty && (
        <SigningProcedureDialog
          party={wizardParty}
          targetParties={parties}
          onSaved={sign}
          onClose={() => setWizardParty(undefined)}
        />
      )}
    </div>
  );
}
